import type {
  VersionForwardMatchKind,
  VersionForwardReviewItem,
} from "@/apis/dsd/types";
import { ModalWrapper } from "@/components/modals/modal-wrapper";
import queries from "@/queries";
import { popModal } from "@/stores/modal";
import IconButton from "@/toggle-ui-kit/icon-button";
import { useQuery } from "@tanstack/react-query";
import { Loader2, X } from "lucide-react";
import { useEffect } from "react";
import {
  ChangeReviewCategory,
  ChangeReviewModal,
  ChangeReviewRow,
} from "./change-review-modal";

// 버전 포워드 표 분류(백엔드)를 모달 범주로 매핑. EXACT 만 "변경 없음" 으로 바뀌고
// 나머지는 1:1.
const KIND_TO_CATEGORY: Record<VersionForwardMatchKind, ChangeReviewCategory> =
  {
    exact: "no_change",
    values_changed: "values_changed",
    structure_changed: "structure_changed",
    unmatched: "unmatched",
  };

// 본문(재무상태표 등)은 이름이 있고, 주석은 "6-1 공정가치금융자산"처럼 번호로 시작한다.
// 이 모달은 주석만 다룬다.
const isNoteName = (name: string): boolean => /^\s*\d/.test(name);

const toRows = (items: VersionForwardReviewItem[]): ChangeReviewRow[] => {
  const notes: VersionForwardReviewItem[] = [];
  for (const item of items) {
    if (isNoteName(item.name)) notes.push(item);
  }
  notes.sort((a, b) => a.name.localeCompare(b.name, "ko", { numeric: true }));

  const rows: ChangeReviewRow[] = [];
  for (const item of notes) {
    rows.push({
      tableName: item.name.replace(/:\s*$/, ""),
      category: KIND_TO_CATEGORY[item.kind],
      // 직전본에 짝이 없는 새 주석은 태깅이 아직 없어 XBRL 작업이 필요하다.
      needsXbrlWork: item.kind === "unmatched",
    });
  }
  return rows;
};

const ShellHeader = () => (
  <div className="flex items-center justify-between px-6 pt-5">
    <h2 className="headline-2-semibold text-label-normal">변경 사항 확인</h2>
    <IconButton
      variant="transparent"
      icon={<X className="size-4" />}
      onClick={popModal}
      aria-label="닫기"
    />
  </div>
);

/**
 * 버전 포워드 직후 뜨는 "변경 사항 확인" 모달의 컨테이너.
 *
 * 버전 포워드가 실제로 쓰는 표(청크) 단위 대사를 백엔드에서 읽기 전용으로 재실행해
 * trg 표별 변경 분류를 받아온다. dsd-dsd 대사의 `<table>`/`<p>` 원자 단위와 달리 태깅
 * 단위(표)와 개수가 일치한다. srcFileId/trgFileId 는 "변경 내역 전체 보기"(상세 HTML
 * 대사)로 넘길 업로드 파일 짝.
 */
export const ChangeReviewModalConnected = ({
  srcWorkbookId,
  trgWorkbookId,
  srcFileId,
  trgFileId,
}: {
  srcWorkbookId: string;
  trgWorkbookId: string;
  srcFileId?: string;
  trgFileId?: string;
}) => {
  const { data, isPending, isError } = useQuery({
    ...queries.versionForwardReview(srcWorkbookId, trgWorkbookId),
    enabled: Boolean(srcWorkbookId && trgWorkbookId),
  });

  // 대사 실패는 조용히 넘긴다 — 업로드 자체는 성공했으므로 에러 모달로 방해하지 않는다.
  useEffect(() => {
    if (isError) popModal();
  }, [isError]);

  if (isError) return null;

  if (isPending || !data) {
    return (
      <ModalWrapper className="flex h-[240px] w-[600px] max-w-[calc(100vw-48px)] flex-col gap-0 overflow-hidden rounded-[8px] border-line-normal bg-white p-0 shadow-xxlarge">
        <ShellHeader />
        <div className="flex flex-1 flex-col items-center justify-center gap-3">
          <Loader2 className="size-6 animate-spin text-label-alternative" />
          <p className="body-2-normal-medium text-label-alternative">
            변경 사항을 확인하는 중입니다...
          </p>
        </div>
      </ModalWrapper>
    );
  }

  return (
    <ChangeReviewModal
      rows={toRows(data.items)}
      srcFileId={srcFileId}
      trgFileId={trgFileId}
      onTagNow={popModal}
    />
  );
};
